/**
 * My Account scope/audience helpers. The SPA app's domain + clientId are delivered from the backend
 * via AppDefaultInfoContext (coreSpaConfiguration); the My Account audience and scopes are derived
 * on the client from that domain, so they live here rather than in backend config.
 */

// Full authentication-methods CRUD scope set. Only read is exercised in this slice, but the full set
// is requested up front so the downstream management UI needs no re-consent (design decision).
// Underscores confirmed against the tenant by the prototype.
export const MY_ACCOUNT_SCOPES = [
    "read:me:authentication_methods",
    "create:me:authentication_methods",
    "update:me:authentication_methods",
    "delete:me:authentication_methods",
] as const;

/** Space-delimited scope string for `authorizationParams.scope`. */
export const getMyAccountScope = (): string => MY_ACCOUNT_SCOPES.join(" ");

/**
 * My Account audience is `https://{domain}/me/`. The same `domain` must be used to obtain the token
 * and as the audience value; the SDK derives the My Account endpoint base from the `Auth0Provider`
 * `domain` itself, so no separate base-URL helper is needed (design Key Risks — domain consistency).
 */
export const getMyAccountAudience = (domain: string): string => `https://${domain}/me/`;

/**
 * Client-side elevated-access window for My Account MFA editing, in seconds (10 minutes).
 * Passed as `max_age` to getAccessTokenWithPopup so Auth0 forces fresh authentication when the
 * user's last auth_time is older than this, and used by useMyAccountMfa to expire elevation
 * proactively.
 *
 * MUST stay aligned with the tenant My Account API access-token `exp` (also set to 10 min on the
 * tenant). A mismatch — e.g. the tenant raised to 30 min — would let a memory-resident token
 * outlive this UI window and weaken the boundary. Change both together.
 */
export const MY_ACCOUNT_STEP_UP_MAX_AGE_SECONDS = 600;
